// src/components/hooks/useTrabajadorDetalles.ts

import { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

/* ============================================
   TIPOS
============================================= */
export interface TrabajadorDetalles {
  id_usuario: string;
  id_empresa: string;
  nombre_completo: string;
  email?: string;
  telefono?: string;
  foto?: string;
  intervalo_cita?: number;
  confirmado?: boolean;
  activo: boolean;
  autocompletar_citas?: boolean;
  autocompletar_horas?: number;
  autocompletar_desde?: string | null;
  autocompletar_tipo_pago?: string;
  es_incluido_plan?: boolean;
  cubierto_por_promo?: boolean;
  cancelacion_programada?: boolean;
  stripe_status?: string | null;
  tiene_acceso?: boolean;
  fecha_fin_acceso?: string | null;
}

export interface TrabajadorCombo {
  id_usuario: string;
  id_empresa: string;
  nombre: string;
  email?: string;
  foto?: string | null;
}

export interface TrabajadorPorServicio {
  id_usuario: string;
  id_empresa: string;
  nombre: string;
  email?: string;
  foto?: string | null;
  id_servicio: string;
  nombre_servicio: string;
  precio?: number;
  duracion_minutos?: number;
}

/* ============================================
   1) DETALLES DE UN TRABAJADOR
============================================= */
export function useTrabajadorDetalles(idUsuario: string, idEmpresa: string) {
  const [perfil, setPerfil] = useState<TrabajadorDetalles | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPerfil = useCallback(async () => {
    if (!idUsuario || !idEmpresa) {
      setPerfil(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const { data, error } = await supabase
      .from('vista_trabajadores_detalles')
      .select('*')
      .eq('id_usuario', idUsuario)
      .eq('id_empresa', idEmpresa)
      .maybeSingle();

    if (error) {
      Sentry.captureException(error, { extra: { idUsuario, idEmpresa } });
      setError('Error al cargar los detalles del trabajador');
      toast.error('Error al cargar los detalles del trabajador');
      setPerfil(null);
    } else {
      setPerfil(data as TrabajadorDetalles);
    }

    setLoading(false);
  }, [idUsuario, idEmpresa]);

  useEffect(() => {
    fetchPerfil();
  }, [fetchPerfil]);

  return { perfil, loading, error, refetch: fetchPerfil };
}

/* ============================================
   2) TRABAJADORES POR EMPRESA (COMBOS)
============================================= */
export function useTrabajadoresPorEmpresa(idEmpresa: string) {
  const [trabajadores, setTrabajadores] = useState<TrabajadorCombo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTrabajadores = useCallback(async () => {
    if (!idEmpresa) {
      setTrabajadores([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const { data, error } = await supabase
      .from('vista_cb_trabajadores')
      .select('*')
      .eq('id_empresa', idEmpresa)
      .order('nombre', { ascending: true });

    if (error) {
      Sentry.captureException(error, { extra: { idEmpresa } });
      setError('Error al cargar los trabajadores');
      toast.error('Error al cargar los trabajadores');
      setTrabajadores([]);
    } else {
      setTrabajadores(data as TrabajadorCombo[]);
    }

    setLoading(false);
  }, [idEmpresa]);

  useEffect(() => {
    fetchTrabajadores();
  }, [fetchTrabajadores]);

  return { trabajadores, loading, error, refetch: fetchTrabajadores };
}

/* ============================================
   3) TRABAJADORES POR SERVICIO
============================================= */
export function useTrabajadoresPorServicio(
  idServicio: string,
  idEmpresa?: string
) {
  const [trabajadores, setTrabajadores] = useState<TrabajadorPorServicio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTrabajadores = useCallback(async () => {
    if (!idServicio) {
      setTrabajadores([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    let query = supabase
      .from('vista_trabajadores_por_servicio')
      .select('*')
      .eq('id_servicio', idServicio);

    if (idEmpresa) query = query.eq('id_empresa', idEmpresa);

    const { data, error } = await query.order('nombre', { ascending: true });

    if (error) {
      Sentry.captureException(error, { extra: { idServicio, idEmpresa } });
      setError('Error al cargar trabajadores por servicio');
      toast.error('Error al cargar trabajadores por servicio');
      setTrabajadores([]);
    } else {
      setTrabajadores(data as TrabajadorPorServicio[]);
    }

    setLoading(false);
  }, [idServicio, idEmpresa]);

  useEffect(() => {
    fetchTrabajadores();
  }, [fetchTrabajadores]);

  return { trabajadores, loading, error, refetch: fetchTrabajadores };
}

/* ============================================
   4) TRABAJADORES DETALLADOS POR EMPRESA
============================================= */
export function useTrabajadoresDetallesEmpresa(idEmpresa: string) {
  const [trabajadores, setTrabajadores] = useState<TrabajadorDetalles[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTrabajadores = useCallback(async () => {
    if (!idEmpresa) {
      setTrabajadores([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const { data, error } = await supabase
      .from('vista_trabajadores_detalles')
      .select('*')
      .eq('id_empresa', idEmpresa);

    if (error) {
      Sentry.captureException(error, { extra: { idEmpresa } });
      setError('Error al cargar los trabajadores');
      toast.error('Error al cargar los trabajadores');
      setTrabajadores([]);
    } else {
      setTrabajadores([...(data as TrabajadorDetalles[])]);
    }

    setLoading(false);
  }, [idEmpresa]);

  useEffect(() => {
    fetchTrabajadores();
  }, [fetchTrabajadores]);

  return { trabajadores, loading, error, refetch: fetchTrabajadores };
}
